import React, { useEffect, useState } from 'react'

import type { AuthInstance, MinimalAuthTokens } from '../types/auth'

interface CreateAuthProviderParams<Role extends string> {
  storageKey: string
  authInstance: AuthInstance
  // Hook passed from app layer to avoid importing app-specific hooks
  useInactivityLogout: (onLogout: () => void, isActive: boolean) => void
  decodeToken: (token: string) => { role: Role; userId: string }
}

interface AuthContextType<Role extends string> {
  auth: { token: string; refreshToken: string; role: Role; userId: string } | null
  isLoading: boolean
  setAuth: React.Dispatch<
    React.SetStateAction<{ token: string; refreshToken: string; role: Role; userId: string } | null>
  >
  login: (username: string, password: string, secret?: string) => Promise<boolean>
  signout: () => void
  resetPassword: (password: string, token: string) => Promise<boolean>
  decodeToken: (token: string) => { role: Role; userId: string }
}

export function createAuthProvider<Role extends string>({
  storageKey,
  authInstance,
  useInactivityLogout,
  decodeToken,
}: CreateAuthProviderParams<Role>) {
  const AuthContext = React.createContext<AuthContextType<Role>>(null!)

  function AuthProvider({ children }: { children: React.ReactNode }) {
    const [auth, setAuth] = useState<{
      token: string
      refreshToken: string
      role: Role
      userId: string
    } | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
      setIsLoading(true)
      const authData = localStorage.getItem(storageKey)
      if (authData) {
        try {
          const parsedAuth: MinimalAuthTokens = JSON.parse(authData)
          const { role, userId } = decodeToken(parsedAuth.token)
          setAuth({ ...parsedAuth, role, userId })
        } catch (error) {
          localStorage.removeItem(storageKey)
          throw error instanceof Error ? error : new Error('Failed to restore auth state')
        }
      }
      setIsLoading(false)
    }, [])

    useEffect(() => {
      if (auth === null) {
        localStorage.removeItem(storageKey)
      }
      if (auth) {
        const minimal: MinimalAuthTokens = {
          token: auth.token,
          refreshToken: auth.refreshToken,
        }
        localStorage.setItem(storageKey, JSON.stringify(minimal))
      }
    }, [auth])

    const login = async (username: string, password: string, secret?: string) => {
      try {
        const authData = await authInstance.login(username, password, secret)
        const { role, userId } = decodeToken(authData.token)
        setAuth({ ...authData, role, userId })
        return true
      } catch (e: unknown) {
        const err = e as { message?: string } | undefined

        if (err?.message && err.message.includes('Access denied')) throw new Error(err.message)
        if (err?.message === 'MFA required') throw new Error(err.message)
        if (err?.message === 'MFA failed') throw new Error(err.message)
        if (err?.message === 'MFA setup required') throw new Error(err.message)
        return false
      }
    }

    const signout = () => {
      setAuth(null)
      localStorage.removeItem('selectedGroupId')
      localStorage.removeItem('selectedCompanyId')
      localStorage.removeItem('dashboardMode')
      localStorage.removeItem(storageKey)
    }

    const resetPassword = async (password: string, token: string) => {
      try {
        await authInstance.resetPassword(password, token)
        return true
      } catch {
        return false
      }
    }

    useInactivityLogout(signout, !!auth)

    const value: AuthContextType<Role> = {
      auth,
      isLoading,
      setAuth,
      login,
      signout,
      resetPassword,
      decodeToken,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  }

  function useAuth() {
    const context = React.useContext(AuthContext)
    if (!context) {
      throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
  }

  return { AuthProvider, useAuth }
}
